import { FormResponse, SystemAction } from "./responses";
import { PageData, UserSpecificPageData } from "./generic";
import { UserGroup, User } from "./auth";
import { AppData } from "./app";
import { Organization } from "./organization";
import { AGGREGATION_OPERATOR } from "./operator";
import { FormRuleData } from "@dialogs/rule-dialog";

export const enum AppAndFormAction {
  PUBLISH = "PUBLISH",
  UNPUBLISH = "UNPUBLISH",
  COPY = "COPY",
  DELETE = "DELETE",
  ADD_MP = "ADD_MP",
}

export interface IWorkingHours {
  weekData?: Record<string, [number, number][]>;
}

export const enum AppAndFormStatus {
  PUBLISHED = "PUBLISHED",
  UNPUBLISHED = "UNPUBLISHED",
  DRAFT = "DRAFT",
  ADD_MP = "ADD_MP",
}
export interface Category {
  _id: string;
  createdBy: Pick<CreatedBy, "name">;
  createdAt: string;
  categoryName: string;
  noOfApps: number;
  categoryDescription: string;
}

export interface CreatedBy {
  _id: string;
  name: string;
  email: string;
  profilePicture: string;
}

export interface Timezone {
  Id: string;
  Name: string;
}

export interface CountryAndDialCode {
  _id: string;
  dial_code: string;
  flag: string;
  name: string;
}
export interface CurrencyCode {
  code: string;
  name: string;
  symbolNative: string;
}

export interface CountryAndTimezone extends CountryAndDialCode {
  timeZone: {
    WindowsTimeZones: Timezone[];
  };
}
export interface ActionParams {
  appId: string;
  formId: string;
  templateId: string;
  pageId: string;
}

export type MessageMetaData = {
  [ruleId: string]: {
    successMessage?: string;
    failureMessage?: string;
  };
};

export type FormResponseMetaData = {
  _id: string;
  rules: FormRuleData[];
  systemActions: SystemAction[];
  responseData: FormResponse;
  createdAt: string;
  updatedAt?: string;
  createdBy?: User;
  // Data Model related props
  appId?: string;
  formId?: string;
};

export type ColumnCalculation = { [key in AGGREGATION_OPERATOR]: Record<string, number> | null };

export type ListResponses = {
  columnCalc?: {
    combined?: ColumnCalculation;
    perPage?: ColumnCalculation;
  };
  page: number;
  totalPages: number;
  pageSize: number;
  totalData: number;
  messageMetaData?: MessageMetaData;
  responses: FormResponseMetaData[];
  calendarResponseMetaData?: {
    responseIds: string[];
    value: string;
  }[];
};
export interface Entity {
  _id: string;
  entityName: string;
  parentEntityId?: string;
  hierarchyName: string;
  createdBy: CreatedBy;
  createdAt: string;
  parentTree: string[];
  workingHours: IWorkingHours;
}
export interface ListEntity extends PageData {
  entities: Entity[];
}
export interface ListUsers extends PageData, UserSpecificPageData {
  users: User[];
}
export interface ListApp extends PageData {
  apps: AppData[];
}
export interface ListGroup extends PageData {
  groups: UserGroup[];
}
export enum PermissionTypes {
  ACTION_CREATE = "action.create",
  ACTION_DELETE = "action.delete",
  ACTION_READ = "action.read",
  ACTION_UPDATE = "action.update",

  APP_CREATE = "app.create",
  APP_DELETE = "app.delete",
  APP_READ = "app.read",
  APP_UPDATE = "app.update",

  COMPANY_READ = "company.read",
  COMPANY_UPDATE = "company.update",
  COMPANY_CREATE = "company.create",

  CATEGORY_CREATE = "category.create",
  CATEGORY_DELETE = "category.delete",
  CATEGORY_READ = "category.read",
  CATEGORY_UPDATE = "category.update",

  FORM_CREATE = "form.create",
  FORM_DELETE = "form.delete",
  FORM_READ = "form.read",
  FORM_UPDATE = "form.update",
  FORM_CLONE = "form.clone",

  MENU_CREATE = "menu.create",
  MENU_DELETE = "menu.delete",
  MENU_READ = "menu.read",
  MENU_UPDATE = "menu.update",

  USER_READ = "user.read",
  USER_CREATE = "user.create",
  USER_UPDATE = "user.update",
  USER_DELETE = "user.delete",

  GROUP_READ = "group.read",
  GROUP_CREATE = "group.create",
  GROUP_UPDATE = "group.update",
  GROUP_DELETE = "group.delete",

  ENTITY_READ = "entity.read",
  ENTITY_CREATE = "entity.create",
  ENTITY_UPDATE = "entity.update",
  ENTITY_DELETE = "entity.delete",

  RESPONSE_TEMPLATE_CREATE = "responseTemplate.create",
  RESPONSE_TEMPLATE_DELETE = "responseTemplate.delete",
  RESPONSE_TEMPLATE_READ = "responseTemplate.read",
  RESPONSE_TEMPLATE_UPDATE = "responseTemplate.update",
  RESPONSE_TEMPLATE_CLONE = "responseTemplate.clone",

  RULE_CREATE = "rule.create",
  RULE_UPDATE = "rule.update",
  RULE_READ = "rule.read",
  RULE_DELETE = "rule.delete",
  RULE_CLONE = "rule.clone",

  LAYOUT_CREATE = "layout.create",
  LAYOUT_DELETE = "layout.delete",
  LAYOUT_CLONE = "layout.clone",

  MARKETPLACE_READ = "marketplace.read",
  MARKETPLACE_ADD = "marketplace.create",

  DASHBOARD_CREATE = "dashboard.create",
  DASHBOARD_UPDATE = "dashboard.update",
  DASHBOARD_READ = "dashboard.read",
  DASHBOARD_DELETE = "dashboard.delete",
  DASHBOARD_CLONE = "dashboard.clone",
}
export interface MarketPlaceReview {
  createdBy: {
    name: string;
    profilePicture: string;
  };
  company: Organization;
  createdAt: string;
  _id?: string;
  comment?: string;
  rating: number;
}
export interface MarketPlaceAppAggregateReview {
  averageRatings: number;
  starRatingMap: Record<number, number>;
  totalRatings: number;
  totalReviews: number;
}
export interface MarketPlaceApp {
  _id: string;
  appName: string;
  appDescription: string;
  appDetail: MarketPlaceAppDetails;
  company: Organization;
  reviews: MarketPlaceAppAggregateReview;
}
export interface IMarketPlaceCategory {
  categoryDescription?: string;
  categoryName: string;
  createdAt: string;
  _id: string;
  apps: MarketPlaceApp[];
  numberOfApps: {
    approved: number;
  };
}
export interface MarketPlaceAppDetails {
  _id: string;
  updatedAt: string;
  tags: string[];
  screenshots: string[];
  isInstalled: boolean;
  createdAt: string;
  coverImage: string;
  details: string;
  app: MarketPlaceApp;
}

export type UserDialogData = {
  allUsers: boolean;
  users: string[];
  allAdmins: boolean;
};

export type ListSenderEmail = {
  emailId: string;
  _id: string;
  name: string;
};
